import { useEffect, useState } from "react";
import { adminListProps, adminDelete, adminUpdate } from "../lib/api";
import { useAuth } from "../auth";
import { Link, useNavigate } from "react-router-dom";
import { Plus, Edit3, Trash2, Star, LogOut, Home as HomeIcon, Building2, ShieldCheck } from "lucide-react";
import { formatTRY, TRANSACTION_LABEL, PROPERTY_TYPE_LABEL } from "../lib/config";
import { toast } from "sonner";

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    adminListProps().then(setItems).catch(() => setItems([])).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const remove = async (id, title) => {
    if (!window.confirm(`"${title}" ilanı silinsin mi?`)) return;
    await adminDelete(id);
    toast.success("İlan silindi");
    load();
  };

  const toggleFeatured = async (p) => {
    await adminUpdate(p.id, { ...p, featured: !p.featured });
    toast.success(p.featured ? "Öne çıkanlardan kaldırıldı" : "Öne çıkanlara eklendi");
    load();
  };

  const setStatus = async (p, status) => {
    await adminUpdate(p.id, { ...p, status });
    toast.success("Durum güncellendi");
    load();
  };

  const doLogout = async () => { await logout(); nav("/"); };

  return (
    <div data-testid="admin-dashboard" className="min-h-screen bg-slate-100">
      <div className="bg-white border-b border-slate-200 sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between h-16">
          <Link to="/" data-testid="admin-dashboard-home-link" className="flex items-center gap-3 group">
            <div className="w-9 h-9 rounded-lg bg-slate-900 flex items-center justify-center group-hover:bg-[#C0562F] transition-colors"><Building2 className="w-4 h-4 text-white" /></div>
            <div>
              <div className="font-display text-slate-900 leading-tight group-hover:text-[#C0562F] transition-colors">SANTRAL EMLAK · Admin</div>
              <div className="text-[10px] tracking-widest uppercase text-slate-500">{user?.email}</div>
            </div>
          </Link>
          <div className="flex items-center gap-2">
            <Link to="/admin/yoneticiler" data-testid="admin-users-link" className="inline-flex items-center gap-1.5 text-sm text-slate-600 hover:text-slate-900 px-2 sm:px-3 py-2"><ShieldCheck className="w-4 h-4" /><span className="hidden sm:inline">Yöneticiler</span></Link>
            <Link to="/" className="hidden sm:inline-flex items-center gap-1.5 text-sm text-slate-600 hover:text-slate-900 px-3 py-2"><HomeIcon className="w-4 h-4" />Siteye Dön</Link>
            <button onClick={doLogout} data-testid="admin-logout" className="inline-flex items-center gap-1.5 text-sm px-3 py-2 rounded-full border border-slate-200 hover:bg-slate-50"><LogOut className="w-4 h-4" /><span className="hidden sm:inline">Çıkış</span></button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
          <div>
            <div className="label-caps mb-2">İlan Yönetimi</div>
            <h1 className="font-display text-3xl text-slate-900">Tüm İlanlar ({items.length})</h1>
          </div>
          <Link to="/admin/ilan/yeni" data-testid="admin-new-prop" className="btn-accent inline-flex items-center gap-2 px-5 py-3 rounded-full text-sm font-semibold"><Plus className="w-4 h-4" /> Yeni İlan Ekle</Link>
        </div>

        {loading ? <div className="text-slate-500">Yükleniyor…</div> : (
          <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm min-w-[720px]">
                <thead className="bg-slate-50 text-slate-500 text-[11px] tracking-widest uppercase">
                  <tr>
                    <th className="text-left p-4">İlan</th>
                    <th className="text-left p-4">Tip</th>
                    <th className="text-left p-4">Konum</th>
                    <th className="text-left p-4">Fiyat</th>
                    <th className="text-left p-4">Durum</th>
                    <th className="text-right p-4">İşlem</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((p) => (
                    <tr key={p.id} data-testid={`admin-row-${p.id}`} className="border-t border-slate-100 hover:bg-slate-50/60">
                      <td className="p-4">
                        <div className="flex items-center gap-3">
                          <img src={p.images?.[0]} alt="" className="w-14 h-14 rounded-lg object-cover bg-slate-100" />
                          <div>
                            <div className="font-semibold text-slate-900">{p.title}</div>
                            <div className="text-xs text-slate-500">{p.slug}</div>
                          </div>
                        </div>
                      </td>
                      <td className="p-4"><span className="text-xs font-semibold text-slate-700">{TRANSACTION_LABEL[p.transaction_type]} · {PROPERTY_TYPE_LABEL[p.property_type]}</span></td>
                      <td className="p-4 text-slate-700">{p.district}, {p.city}</td>
                      <td className="p-4 font-semibold text-slate-900">{formatTRY(p.price)}</td>
                      <td className="p-4">
                        <select value={p.status} onChange={(e) => setStatus(p, e.target.value)} className="text-xs bg-white border border-slate-200 rounded-md px-2 py-1">
                          <option value="aktif">Aktif</option>
                          <option value="satildi">Satıldı</option>
                          <option value="kiralandi">Kiralandı</option>
                          <option value="pasif">Pasif</option>
                        </select>
                      </td>
                      <td className="p-4">
                        <div className="flex items-center gap-2 justify-end">
                          <button onClick={() => toggleFeatured(p)} title="Öne Çıkar" className={`w-8 h-8 rounded-md flex items-center justify-center ${p.featured ? "bg-amber-100 text-amber-700" : "hover:bg-slate-100 text-slate-500"}`}><Star className="w-4 h-4" /></button>
                          <Link to={`/admin/ilan/${p.id}`} className="w-8 h-8 rounded-md flex items-center justify-center hover:bg-slate-100 text-slate-700" title="Düzenle" data-testid={`admin-edit-${p.id}`}><Edit3 className="w-4 h-4" /></Link>
                          <button onClick={() => remove(p.id, p.title)} className="w-8 h-8 rounded-md flex items-center justify-center hover:bg-red-50 text-red-600" title="Sil" data-testid={`admin-delete-${p.id}`}><Trash2 className="w-4 h-4" /></button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
